import { Box, Typography } from '@mui/material';

import { Wrapper } from './components/Wrapper';

import type { Variant } from '@mui/material/styles/createTypography';
import type { FC, ReactNode } from 'react';

export const INFO_TEMPLATE_TEST_ID = 'info-template';

interface ImageProps {
  width?: number;
  height?: number;
  maxWidth?: number;
}

type InfoTemplateSize = 'sm' | 'md' | 'lg';

interface ChildrenProps {
  role: string;
  viewBox: string;
  preserveAspectRatio: string;
  style: { maxWidth: number };
  'aria-label'?: string;
}

export interface InfoTemplateProps {
  header: ReactNode;
  description: ReactNode;
  action?: ReactNode;
  imageProps?: ImageProps;
  size?: InfoTemplateSize;
  children: FC<ChildrenProps>;
  'data-testid'?: string;
}

interface InfoTemplateTypography {
  header: Variant;
  description: Variant;
}

const TypographySize: Record<InfoTemplateSize, InfoTemplateTypography> = {
  sm: { header: 'subtitle1', description: 'caption' },
  md: { header: 'h6', description: 'body1' },
  lg: { header: 'h5', description: 'body1' },
};

const defaultImageSize = 360;

export const InfoTemplate: FC<InfoTemplateProps> = ({
  header,
  description,
  action = null,
  size = 'sm',
  children,
  imageProps: {
    width = defaultImageSize,
    height = defaultImageSize,
    maxWidth = defaultImageSize,
  } = {},
  'data-testid': dataTestId,
}) => (
  <Wrapper
    data-testid={dataTestId ?? INFO_TEMPLATE_TEST_ID}
    rowGap={size === 'lg' ? 3 : 2}
  >
    {children({
      role: 'img',
      viewBox: `0 0 ${width} ${height}`,
      preserveAspectRatio: 'xMinYMax meet',
      style: { maxWidth },
    })}
    <Box textAlign="center">
      <Typography variant={TypographySize[size].header}>{header}</Typography>
      <Typography
        variant={TypographySize[size].description}
        color="text.secondary"
        marginTop={1}
      >
        {description}
      </Typography>
    </Box>
    {action}
  </Wrapper>
);
